// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { QuestionnaireResponse } from '@medplum/fhirtypes';

const NONE_CODE = 'none';

/**
 * Mutual-exclusion cleanup for the HSAT exclusion checklist on the patient
 * eligibility form. Selecting "None of the above" together with a sibling
 * exclusion is a logical contradiction: the sibling selection wins and "none"
 * is stripped before the QR reaches the bot pipeline.
 *
 * Why pre-submit cleanup rather than instant-visible client mutation:
 * Medplum's QuestionnaireForm renders the checkbox group internally and
 * doesn't expose a per-item override that lets us mutate visible state per
 * toggle. The data-correctness goal (a Condition gets created for real
 * exclusions, not silently dropped because "none" was also marked) is what
 * matters for triage. A future ticket can add visible client-side
 * enforcement; the provider-side TriageForm (Mantine, direct state) already
 * has it via applyNoneOfTheAboveExclusion.
 */
export function stripNoneOfTheAboveIfSiblings(response: QuestionnaireResponse, linkId: string): QuestionnaireResponse {
  if (!response.item) {
    return response;
  }
  return {
    ...response,
    item: response.item.map((item) => {
      if (item.linkId !== linkId || !item.answer) {
        return item;
      }
      const hasNone = item.answer.some((a) => a.valueCoding?.code === NONE_CODE);
      const hasSibling = item.answer.some((a) => a.valueCoding?.code !== NONE_CODE);
      if (hasNone && hasSibling) {
        return { ...item, answer: item.answer.filter((a) => a.valueCoding?.code !== NONE_CODE) };
      }
      return item;
    }),
  };
}
